import { useFormikContext } from 'formik'
import { Box, TextField, useMediaQuery, useTheme } from '@mui/material'
import { useState, useRef, useEffect } from 'react'
import { DayPicker, DateRange } from 'react-day-picker'
import moment from 'moment'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import 'react-day-picker/style.css'
import { IChallengesForm } from 'src/types'

import { useStyles } from '../styles'
import { SPACES } from 'src/constants'

interface FormDateRangePickerProps {
  startField: string
  endField: string
  label?: string
}

export const FormDateRangePicker: React.FC<FormDateRangePickerProps> = ({
  startField,
  endField,
  label = 'Range:',
}) => {
  const theme = useTheme()
  const classes = useStyles()

  const { values, setFieldValue } = useFormikContext<IChallengesForm>()
  const [selected, setSelected] = useState<DateRange | undefined>(undefined)
  const [open, setOpen] = useState<boolean>(false)
  const refOne = useRef<HTMLDivElement | null>(null)

  const isLaptop = useMediaQuery(theme.breakpoints.up('laptop_l'))

  useEffect(() => {
    if (values.start_date && values.end_date) {
      const fromMoment = moment.utc(values.start_date)
      const toMoment = moment.utc(values.end_date)

      setSelected({
        from: fromMoment.toDate(),
        to: toMoment.toDate(),
      })
    }
  }, [values.start_date, values.end_date])

  useEffect(() => {
    const hideOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }

    const hideOnClickOutside = (e: MouseEvent) => {
      if (refOne.current && !refOne.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('keydown', hideOnEscape)
      document.addEventListener('click', hideOnClickOutside)
    }

    return () => {
      document.removeEventListener('keydown', hideOnEscape)
      document.removeEventListener('click', hideOnClickOutside)
    }
  }, [open])

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) {
      setSelected(range)
      return
    }

    const { from: f, to: t } = range
    // grab Y/M/D from the picked Date
    const Yf = f.getFullYear(),
      Mf = f.getMonth(),
      Df = f.getDate()
    const Yt = t.getFullYear(),
      Mt = t.getMonth(),
      Dt = t.getDate()

    // build true UTC-midnight moments
    const utcFrom = moment.utc([Yf, Mf, Df, 12, 0, 0])
    const utcTo = moment.utc([Yt, Mt, Dt, 12, 0, 0])

    setSelected({
      from: utcFrom.toDate(),
      to: utcTo.toDate(),
    })

    setFieldValue(startField, utcFrom)
    setFieldValue(endField, utcTo)
  }

  const displayValue = selected
    ? `${moment.utc(selected.from).format('M/D/YYYY')} - ${moment
        .utc(selected.to)
        .format('M/D/YYYY')} UTC`
    : 'ALL'

  return (
    <Box
      sx={{
        position: 'relative',
        '.rdp-root': {
          backgroundColor: theme.palette.custom.white,
          position: 'absolute',
          top: isLaptop ? 40 : 35,
          left: 0,
          zIndex: 100,
          border: `1px solid ${theme.palette.custom.grey}`,
          borderRadius: '4px',
          boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
          padding: '8px 12px',
          '--rdp-accent-color': theme.palette.custom.primary,
          '--rdp-today-color': theme.palette.custom.primary,
          '--rdp-range_middle-background-color': theme.palette.custom.grey,
          '--rdp-day-height': '36px',
          '--rdp-day-width': '36px',

          '.rdp-month_caption': {
            fontSize: '16px',
            fontWeight: 700,
            '.rdp-chevron': { marginLeft: '8px' },
            '.rdp-dropdown': {
              width: '100%',

              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        '.rdp-root button': {
          fontSize: '14px',
        },
      }}
    >
      <Box
        ref={refOne}
        sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}
      >
        <TextField
          id="challenges-date-range-picker"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((prev) => !prev)
          }}
          sx={{ '.MuiInputBase-input.MuiOutlinedInput-input': { cursor: 'pointer' } }}
          value={displayValue}
          InputProps={{
            readOnly: true,
            style: {
              width: '100%',
              border: `1px solid ${theme.palette.custom.grey}`,
              borderRadius: '15px',
              paddingLeft: SPACES.xxl,
              paddingRight: SPACES.xl,
              fontSize: '12px',
              fontWeight: 700,
              height: isLaptop ? 50 : 40,
              cursor: 'pointer',
            },
            startAdornment: <span className={classes.startAdornment}>{label}</span>,
            endAdornment: open ? (
              <ArrowDropUpIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            ),
          }}
        />

        {open && (
          <DayPicker
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={1}
            captionLayout="dropdown"
          />
        )}
      </Box>
    </Box>
  )
}
